import { DownloadSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useExportMarketShopTransactions } from '@/api/market-shop/transactions/export-market-shop-transactions'
import {
  MARKET_SHOP_TRANSACTIONS_BASE_QUERY_KEY,
  useGetMarketShopTransactions,
} from '@/api/market-shop/transactions/get-market-shop-transactions'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange, MarketShopOrderStatus, MarketShopOrderStatusEnum } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  getMarketShopStatusLabelsMap,
  getMarketShopTransactionsColumns,
  MARKET_SHOP_STATUS_COLORS_MAP,
  MarketShopTransactionStatusFilter,
  marketShopTransactionsInitialFilters,
  marketShopTransactionsQueryFiltersSchema,
} from './market-shop-transactions.constants'

export const MarketShopTransactionsPage = () => {
  const { t } = useTranslation(['market-shop-dashboard'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const isRestaurantMode = !!activeBuilding?.marketShopRestaurantAvailable
  // NOTE: falls back to the browser's local timezone, matching the implicit fallback
  // `Intl.DateTimeFormat` already applies internally when no timezone is passed.
  const timezone = activeBuilding?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'MarketShop',
    fieldType: 'resident',
    rowsQueryKey: [MARKET_SHOP_TRANSACTIONS_BASE_QUERY_KEY],
  })

  const marketShopColumns = useMemo(
    () =>
      getMarketShopTransactionsColumns({
        isRestaurantMode,
        residentColumnSettings,
        t,
        timezone,
      }),
    [timezone, isRestaurantMode, residentColumnSettings, t],
  )

  const searchParams = useSearchParams({
    initialState: marketShopTransactionsInitialFilters,
    schema: marketShopTransactionsQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: marketShopTransactionsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const sort = filters.getFilter('sort')
  const order = filters.getFilter('order')
  const apiSort = sort && order ? `${sort}:${order}` : undefined

  const statusFilter = filters.getFilter('status') as MarketShopTransactionStatusFilter | undefined

  const effectiveStatus: MarketShopTransactionStatusFilter = !isRestaurantMode
    ? 'completed'
    : (statusFilter ?? 'all')

  const filtersState = filters.filters

  const queryParams = {
    buildingId: activeBuilding?.id,
    fromDate: filtersState.fromDate,
    limit: filtersState.limit,
    search: filtersState.search?.trim(),
    skip: filtersState.skip,
    sort: apiSort,
    status: !isRestaurantMode
      ? ('completed' as MarketShopOrderStatus)
      : filtersState.status !== 'all'
        ? filtersState.status
        : undefined,
    toDate: filtersState.toDate,
  }

  const {
    data,
    isFetching: isTransactionsFetching,
    error: transactionsError,
  } = useGetMarketShopTransactions(queryParams)

  const { total = 0, data: transactions = [], countByStatus } = data || {}

  const { mutateAsync: downloadTransactions, isPending: isDownloadPending } =
    useExportMarketShopTransactions()

  const statusTabsOptions: StatusTabOption<MarketShopTransactionStatusFilter>[] = useMemo(() => {
    const statusLabels = getMarketShopStatusLabelsMap(t)

    if (!isRestaurantMode) {
      return [
        {
          color: MARKET_SHOP_STATUS_COLORS_MAP.completed,
          count: countByStatus?.completed,
          label: statusLabels.completed,
          value: 'completed',
        },
      ]
    }

    const allCount = countByStatus
      ? MarketShopOrderStatusEnum.options.reduce((sum, s) => sum + (countByStatus[s] ?? 0), 0)
      : undefined

    return [
      {
        count: allCount,
        label: t('market-shop-dashboard:transactions.statusTabs.all'),
        value: 'all',
      },
      ...MarketShopOrderStatusEnum.options.map((status) => ({
        color: MARKET_SHOP_STATUS_COLORS_MAP[status],
        count: countByStatus?.[status],
        label: statusLabels[status],
        value: status,
      })),
    ]
  }, [isRestaurantMode, countByStatus, t])

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({
      fromDate: range[0]?.getTime(),
      toDate: range[1]?.getTime(),
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleStatusChange = (status: MarketShopTransactionStatusFilter) => {
    filters.setFilter('status', status)
  }

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      order: sortOrder ?? undefined,
      sort: sortBy ?? undefined,
    })
  }

  const handleDownloadCSVClick = () => {
    if (!activeBuilding) return

    let fileName = 'market-shop-transactions.csv'
    const trimmed = activeBuilding.name?.trim()
    if (trimmed) {
      fileName = `${trimmed}-` + fileName
    }

    void downloadTransactions({
      buildingId: activeBuilding.id,
      fileName,
      from: fromDateFilter,
      to: toDateFilter,
    })
  }

  return (
    <TableGroup
      id="market-shop-transactions"
      columns={marketShopColumns}
      data={transactions}
      error={transactionsError}
      // NOTE: Orders with totalAmount = 0 skip Stripe, so transaction Id is null for them
      getRowKey={(transaction) => transaction.orderId}
      loading={isTransactionsFetching}
      sortBy={sort}
      sortOrder={order}
      navigation={{
        actions: (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('market-shop-dashboard:transactions.exportCsv')}
            loading={isDownloadPending}
            disabled={!activeBuilding}
            onClick={handleDownloadCSVClick}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        datePicker: (
          <DatePickerInput
            mode="range"
            placeholder={t('market-shop-dashboard:transactions.dateRangePlaceholder')}
            size="small"
            value={dateRange}
            maxDate={toZonedTime(new Date(), timezone)}
            onRangeChange={handleDateChange}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('market-shop-dashboard:transactions.searchPlaceholder')}
            size="small"
            value={filters.getFilter('search')}
            onChange={handleSearchChange}
          />
        ),
        statusTabs: isRestaurantMode ? (
          <StatusTabs
            options={statusTabsOptions}
            value={effectiveStatus}
            onChange={handleStatusChange}
          />
        ) : undefined,
      }}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      tableFilters={filters}
      onSort={handleSort}
    />
  )
}
